import {
  AutopilotTaskDto,
  TaskCategoryStage,
  TaskCategoryTaskType,
  TaskCategoryWorkflowType,
  TaskStepDto,
} from '@urbanx/agx.activities.client';
import { CampaignDetailModelState } from '@urbanx/agx.campaigns.client';

export interface AgreementComplianceStep {
  task: ResolvedTask;
  step: TaskStepDto;
}

type ResolvedTask = AutopilotTaskDto & { id: string };

const hasTaskId = (task: AutopilotTaskDto | undefined): task is ResolvedTask => Boolean(task?.id);

export const getInteractiveAgreementComplianceTasks = (tasks: AutopilotTaskDto[]) =>
  tasks.filter(
    (task) =>
      !task.isLocked &&
      task.category?.workflowType === TaskCategoryWorkflowType.Campaign &&
      task.category?.stage === TaskCategoryStage.AgreementCompliance
  );

export interface AgreementComplianceTaskLayout {
  pageTasks: ResolvedTask[];
  agreementDetailsSteps: AgreementComplianceStep[];
  commissionStep: AgreementComplianceStep;
}

export interface NswAgreementComplianceTaskLayout extends AgreementComplianceTaskLayout {
  appointmentPeriodSteps: [AgreementComplianceStep, AgreementComplianceStep];
}

type AgreementComplianceTaskType =
  | TaskCategoryTaskType.AgreementDetails
  | TaskCategoryTaskType.AgreementComplianceType
  | TaskCategoryTaskType.AgreementCommission;

interface AgreementComplianceTaskDefinition {
  taskType: AgreementComplianceTaskType;
  stepCount: number;
  page: boolean;
  content?: AgreementComplianceTaskType;
}

interface AgreementComplianceLayoutDefinition {
  tasks: readonly AgreementComplianceTaskDefinition[];
}

interface ResolvedAgreementComplianceTask extends AgreementComplianceTaskDefinition {
  task: ResolvedTask;
  steps: AgreementComplianceStep[];
}

const agreementComplianceLayoutDefinitions: Partial<
  Record<CampaignDetailModelState, AgreementComplianceLayoutDefinition>
> = {
  [CampaignDetailModelState.QLD]: {
    tasks: [
      {
        taskType: TaskCategoryTaskType.AgreementDetails,
        stepCount: 6,
        page: true,
        content: TaskCategoryTaskType.AgreementDetails,
      },
      {
        taskType: TaskCategoryTaskType.AgreementComplianceType,
        stepCount: 3,
        page: true,
      },
      {
        taskType: TaskCategoryTaskType.AgreementCommission,
        stepCount: 1,
        page: true,
        content: TaskCategoryTaskType.AgreementCommission,
      },
    ],
  },
  [CampaignDetailModelState.NSW]: {
    tasks: [
      {
        taskType: TaskCategoryTaskType.AgreementDetails,
        stepCount: 9,
        page: true,
        content: TaskCategoryTaskType.AgreementDetails,
      },
      {
        taskType: TaskCategoryTaskType.AgreementComplianceType,
        stepCount: 2,
        page: true,
        content: TaskCategoryTaskType.AgreementComplianceType,
      },
      {
        taskType: TaskCategoryTaskType.AgreementCommission,
        stepCount: 1,
        page: false,
        content: TaskCategoryTaskType.AgreementCommission,
      },
    ],
  },
};

const layoutDefinitionForState = (state: CampaignDetailModelState | undefined) => {
  if (!state) {
    throw new Error('Agreement Compliance state is missing');
  }

  const definition = agreementComplianceLayoutDefinitions[state];

  if (!definition) {
    throw new Error(`Agreement Compliance layout is not supported for ${state}`);
  }

  return definition;
};

const taskByType = (tasks: AutopilotTaskDto[], taskType: TaskCategoryTaskType) => {
  const task = tasks.find((candidate) => candidate.category?.taskType === taskType);

  if (!hasTaskId(task)) {
    throw new Error(`Agreement Compliance task ${taskType} is missing`);
  }

  return task;
};

const resolveTask = (
  tasks: AutopilotTaskDto[],
  definition: AgreementComplianceTaskDefinition
): ResolvedAgreementComplianceTask => {
  const task = taskByType(tasks, definition.taskType);
  if (!task.taskSteps) {
    throw new Error(`Agreement Compliance task ${definition.taskType} has no steps`);
  }

  const taskSteps = [...task.taskSteps].sort(
    (left, right) => (left.order ?? 0) - (right.order ?? 0)
  );
  const hasExpectedOrder = taskSteps.every((step, index) => step.order === index + 1);

  if (taskSteps.length !== definition.stepCount || !hasExpectedOrder) {
    throw new Error(
      `Agreement Compliance task ${definition.taskType} does not match its ${definition.stepCount}-step layout`
    );
  }

  return {
    ...definition,
    task,
    steps: taskSteps.map((step) => ({ task, step })),
  };
};

const resolvedTaskByContent = (
  tasks: ResolvedAgreementComplianceTask[],
  content: NonNullable<ResolvedAgreementComplianceTask['content']>
) => {
  const task = tasks.find((candidate) => candidate.content === content);

  if (!task) {
    throw new Error(`Agreement Compliance ${content} task is not configured`);
  }

  return task;
};

const resolveSingleStep = (task: ResolvedAgreementComplianceTask) => {
  const [step] = task.steps;

  if (!step || task.steps.length !== 1) {
    throw new Error(`Agreement Compliance task ${task.task.category?.taskType} must have one step`);
  }

  return step;
};

const resolveAgreementComplianceTaskLayout = (
  tasks: AutopilotTaskDto[],
  definition: AgreementComplianceLayoutDefinition
) => {
  const resolvedTasks = definition.tasks.map((taskDefinition) =>
    resolveTask(tasks, taskDefinition)
  );
  const configuredTaskTypes = new Set(resolvedTasks.map(({ taskType }) => taskType));

  if (configuredTaskTypes.size !== tasks.length) {
    throw new Error('Agreement Compliance task set does not match its configured layout');
  }

  const appointmentPeriodTask = resolvedTasks.find(
    ({ content }) => content === TaskCategoryTaskType.AgreementComplianceType
  );

  return {
    pageTasks: resolvedTasks.filter(({ page }) => page).map(({ task }) => task),
    agreementDetailsSteps: resolvedTaskByContent(
      resolvedTasks,
      TaskCategoryTaskType.AgreementDetails
    ).steps,
    appointmentPeriodSteps: appointmentPeriodTask ? appointmentPeriodTask.steps : [],
    commissionStep: resolveSingleStep(
      resolvedTaskByContent(resolvedTasks, TaskCategoryTaskType.AgreementCommission)
    ),
  };
};

export const resolveQldAgreementComplianceTaskLayout = (
  tasks: AutopilotTaskDto[]
): AgreementComplianceTaskLayout =>
  resolveAgreementComplianceTaskLayout(
    tasks,
    layoutDefinitionForState(CampaignDetailModelState.QLD)
  );

export const resolveNswAgreementComplianceTaskLayout = (
  tasks: AutopilotTaskDto[]
): NswAgreementComplianceTaskLayout => {
  const layout = resolveAgreementComplianceTaskLayout(
    tasks,
    layoutDefinitionForState(CampaignDetailModelState.NSW)
  );
  const [appointmentStartStep, appointmentEndStep] = layout.appointmentPeriodSteps;

  if (!appointmentStartStep || !appointmentEndStep) {
    throw new Error('NSW Agreement Compliance appointment period steps are missing');
  }

  return {
    ...layout,
    appointmentPeriodSteps: [appointmentStartStep, appointmentEndStep],
  };
};

const canAttemptAgreementComplianceCompletionForLayout = (
  tasks: AutopilotTaskDto[],
  definition: AgreementComplianceLayoutDefinition
) => {
  const { commissionStep } = resolveAgreementComplianceTaskLayout(tasks, definition);

  return tasks.every(
    (task) =>
      (task.taskSteps?.length ?? 0) > 0 &&
      (task.taskSteps ?? []).every(
        (step) =>
          (task === commissionStep.task && step.order === commissionStep.step.order) ||
          step.isCompleted
      )
  );
};

export const canAttemptAgreementComplianceCompletion = (
  tasks: AutopilotTaskDto[],
  state: CampaignDetailModelState | undefined
) => canAttemptAgreementComplianceCompletionForLayout(tasks, layoutDefinitionForState(state));

export const createAgreementComplianceTaskModalFlow = (
  state: CampaignDetailModelState | undefined
) => {
  const definition = layoutDefinitionForState(state);

  return {
    resolvePageTasks: (tasks: AutopilotTaskDto[]) =>
      resolveAgreementComplianceTaskLayout(tasks, definition).pageTasks,
    resolvePersistenceTasks: (tasks: AutopilotTaskDto[]) => tasks,
    canComplete: (tasks: AutopilotTaskDto[]) =>
      canAttemptAgreementComplianceCompletionForLayout(tasks, definition),
  };
};
